import { EntityLevel } from '@/composables/useEntityLevel'
import { compileDashboardFeature, DashboardComponentType } from '../dashboard'
import { FormatOptions } from '@/components/AnalyticsDashboard/formatter'
import { co2ColorScale, humidityColorScale, temperatureColorScale } from './colorScales'

const enabledEntityLevelsColoredEntities: EntityLevel[] = [
  'COMPLEX',
  'REAL_ESTATE',
  'CONSTRUCTION',
  'FLOOR',
  'LEVEL',
  'SPACE'
]

const temperatureFormat: FormatOptions = {
  format: {
    type: 'intl-number',
    maximumFractionDigits: 1
  },
  steps: [
    {
      format: {
        type: 'locale-format-string',
        format: {
          sv: '%s °C',
          en: '%s °C'
        }
      }
    }
  ]
}

const humidityFormat: FormatOptions = {
  format: {
    type: 'intl-number',
    maximumFractionDigits: 1
  },
  steps: [
    {
      format: {
        type: 'locale-format-string',
        format: {
          sv: '%s %',
          en: '%s %'
        }
      }
    }
  ]
}

const co2Format: FormatOptions = {
  format: {
    type: 'intl-number',
    maximumFractionDigits: 0
  },
  steps: [
    {
      format: {
        type: 'locale-format-string',
        format: {
          sv: '%s ppm',
          en: '%s ppm'
        }
      }
    }
  ]
}

export const indoorClimate2 = compileDashboardFeature({
  name: 'indoor-climate',
  title: {
    sv: 'Inomhus&shy;klimat',
    en: 'Indoor climate'
  },
  dataSectionTitle: {
    sv: 'Inomhusklimat',
    en: 'Indoor climate'
  },
  icon: 'ThermometerIcon',
  enabled: false,
  defaultFilters: {
    calendar: {
      quickSelect: 'last30days'
    },
    defaultDateSelection: 'last24h',
    onlyBusinessDays: false,
    onlyBusinessHours: false,
    dateSelections: [
      'now',
      'last24h',
      'yesterday',
      'last7d',
      'last30d',
      'lastquarter',
      'last12m',
      'currentyear',
      'lastyear',
      'custom'
    ]
  },
  measurements: [
    {
      name: 'indoor_climate_10_minutes',
      field: {
        name: 'temperature_avg',
        expression: {
          type: 'avg',
          exp: {
            type: 'field',
            name: 'temperature_avg'
          }
        }
      },
      title: {
        sv: 'Medeltemperatur',
        en: 'Average temperature'
      },
      options: {
        colorScale: temperatureColorScale,
        enabledEntityLevels: enabledEntityLevelsColoredEntities,
        format: temperatureFormat
      }
    },
    {
      name: 'indoor_climate_10_minutes',
      field: {
        name: 'temperature_stddev',
        expression: {
          type: 'stddev',
          exp: {
            type: 'field',
            name: 'temperature_avg'
          }
        }
      },
      title: {
        sv: 'Temperaturavvikelse',
        en: 'Temperature deviation'
      },
      options: {
        color: '#F759AB',
        enabledEntityLevels: enabledEntityLevelsColoredEntities,
        format: {
          format: {
            type: 'intl-number',
            maximumFractionDigits: 2
          },
          steps: [
            {
              format: {
                type: 'format-string',
                format: '%s °C'
              }
            }
          ]
        },
        legend: { type: 'slider' }
      }
    },
    {
      name: 'indoor_climate_10_minutes',
      field: {
        name: 'temperature_hist',
        expression: {
          type: 'histogram',
          min: 18,
          max: 30,
          nbuckets: 30 - 18,
          exp: {
            type: 'field',
            name: 'temperature_avg'
          }
        }
      },
      title: {
        sv: 'Andel',
        en: 'Percentage'
      },
      options: {
        colorScale: temperatureColorScale,
        enabledEntityLevels: enabledEntityLevelsColoredEntities,
        format: temperatureFormat
      }
    },
    {
      name: 'indoor_climate_10_minutes',
      field: {
        name: 'humidity_avg',
        expression: {
          type: 'avg',
          exp: {
            type: 'field',
            name: 'humidity_avg'
          }
        }
      },
      title: {
        sv: 'Genomsnittlig Luftfuktighet',
        en: 'Average humidity'
      },
      options: {
        colorScale: humidityColorScale,
        enabledEntityLevels: enabledEntityLevelsColoredEntities,
        format: humidityFormat
      }
    },
    {
      name: 'indoor_climate_10_minutes',
      field: {
        name: 'humidity_stddev',
        expression: {
          type: 'stddev',
          exp: {
            type: 'field',
            name: 'humidity_stddev'
          }
        }
      },
      title: {
        sv: 'Luftfuktighetsavvikelse',
        en: 'Humidity deviation'
      },
      options: {
        colorScale: humidityColorScale,
        enabledEntityLevels: enabledEntityLevelsColoredEntities,
        format: humidityFormat
      }
    },
    {
      name: 'indoor_climate_10_minutes',
      field: {
        name: 'humidity_hist',
        expression: {
          type: 'histogram',
          min: 0,
          max: 100,
          nbuckets: 10,
          exp: {
            type: 'field',
            name: 'humidity_avg'
          }
        }
      },
      title: {
        sv: 'Luftfuktighet',
        en: 'Humidity'
      },
      options: {
        colorScale: humidityColorScale,
        enabledEntityLevels: enabledEntityLevelsColoredEntities,
        format: humidityFormat
      }
    },
    {
      name: 'indoor_climate_10_minutes',
      field: {
        name: 'co2_avg',
        expression: {
          type: 'avg',
          exp: {
            type: 'field',
            name: 'co2_avg'
          }
        }
      },
      title: {
        sv: 'Genomsnittlig Koldioxid',
        en: 'Average carbon dioxide'
      },
      options: {
        colorScale: co2ColorScale,
        enabledEntityLevels: enabledEntityLevelsColoredEntities,
        format: co2Format
      }
    },
    {
      name: 'indoor_climate_10_minutes',
      field: {
        name: 'co2_stddev',
        expression: {
          type: 'stddev',
          exp: {
            type: 'field',
            name: 'co2_stddev'
          }
        }
      },
      title: {
        sv: 'Koldioxidavvikelse',
        en: 'Carbon dioxide deviation'
      },
      options: {
        colorScale: co2ColorScale,
        enabledEntityLevels: enabledEntityLevelsColoredEntities,
        format: co2Format
      }
    },
    {
      name: 'indoor_climate_10_minutes',
      field: {
        name: 'co2_hist',
        expression: {
          type: 'histogram',
          min: 0,
          max: 2000,
          nbuckets: 10,
          exp: {
            type: 'field',
            name: 'co2_avg'
          }
        }
      },
      title: {
        sv: 'Koldioxid',
        en: 'Co2'
      },
      options: {
        colorScale: co2ColorScale,
        enabledEntityLevels: enabledEntityLevelsColoredEntities,
        format: co2Format
      }
    }
  ],
  components: [
    {
      type: DashboardComponentType.Kpi,
      name: 'average-temperature',
      measurement: 'indoor_climate_10_minutes.temperature_avg',
      info: {
        sv: 'Genomsnittlig temperatur',
        en: 'Average temperature'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'deviation-temperature',
      measurement: 'indoor_climate_10_minutes.temperature_stddev',
      info: {
        sv: 'Temperaturavvikelse',
        en: 'Temperature deviation'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'average-temp-over-time',
      measurements: ['indoor_climate_10_minutes.temperature_avg'],
      title: {
        sv: 'Temperatur över tid',
        en: 'Temperature over time'
      },
      info: {
        sv: 'Visar temperatur över tid.',
        en: 'Shows temperature over time.'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'average-temp-over-time-per-entity',
      measurements: ['indoor_climate_10_minutes.temperature_avg'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }]
          }
        }
      },
      title: {
        sv: 'Temperatur över tid (per entitet)',
        en: 'Temperature over time (per entity)'
      },
      info: {
        sv: 'Visar temperatur över tid.',
        en: 'Shows temperature over time.'
      }
    },
    {
      type: DashboardComponentType.Histogram,
      name: 'temp-histogram',
      measurements: ['indoor_climate_10_minutes.temperature_hist'],
      title: {
        sv: 'Temperaturfördelning',
        en: 'Temperature distribution'
      },
      info: {
        sv: 'Visar temperaturfördelning.',
        en: 'Shows temperature distribution.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'average-temp-toplist',
      measurements: ['indoor_climate_10_minutes.temperature_avg'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'temperature_avg', direction: 'DESC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: {
        sv: 'Temperatur per entitet',
        en: 'Temperature per entity'
      },
      info: {
        sv: 'Visar temperatur per entitet.',
        en: 'Shows temperature per entity.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'stddev-temp-toplist',
      measurements: ['indoor_climate_10_minutes.temperature_stddev'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'temperature_stddev', direction: 'DESC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: {
        sv: 'Temperaturavvikelse per entitet',
        en: 'Temperature deviation per entity'
      },
      info: {
        sv: 'Visar temperaturavvikelse per entitet.',
        en: 'Shows temperature deviation per entity.'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'average-humidity',
      measurement: 'indoor_climate_10_minutes.humidity_avg',
      info: {
        sv: 'Genomsnittlig luftfuktighet',
        en: 'Average humidity'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'average-humidity-over-time',
      measurements: ['indoor_climate_10_minutes.humidity_avg'],
      title: {
        sv: 'Luftfuktighet över tid',
        en: 'Humidity over time'
      },
      info: {
        sv: 'Visar luftfuktighet över tid.',
        en: 'Shows humidity over time.'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'average-humidity-over-time-per-entity',
      measurements: ['indoor_climate_10_minutes.humidity_avg'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }]
          }
        }
      },
      title: {
        sv: 'Luftfuktighet över tid (per entitet)',
        en: 'Humidity over time (per entity)'
      },
      info: {
        sv: 'Visar luftfuktighet över tid.',
        en: 'Shows humidity over time.'
      }
    },
    {
      type: DashboardComponentType.Histogram,
      name: 'humidity-histogram',
      measurements: ['indoor_climate_10_minutes.humidity_hist'],
      title: {
        sv: 'Luftfuktighetsfördelning',
        en: 'Humidity distribution'
      },
      info: {
        sv: 'Visar luftfuktighetsfördelning.',
        en: 'Shows humidity distribution.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'average-humidity-toplist',
      measurements: ['indoor_climate_10_minutes.humidity_avg'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'humidity_avg', direction: 'DESC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: {
        sv: 'Luftfuktighet per entitet',
        en: 'Humidity per entity'
      },
      info: {
        sv: 'Visar luftfuktighet per entitet.',
        en: 'Shows humidity per entity.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'stddev-humidity-toplist',
      measurements: ['indoor_climate_10_minutes.humidity_stddev'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'humidity_stddev', direction: 'DESC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: {
        sv: 'Luftfuktighetsavvikelse per entitet',
        en: 'Humidity deviation per entity'
      },
      info: {
        sv: 'Visar luftfuktighetsavvikelse per entitet.',
        en: 'Shows humidity deviation per entity.'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'average-co2',
      measurement: 'indoor_climate_10_minutes.co2_avg',
      info: {
        sv: 'Genomsnittlig koldioxidhalt',
        en: 'Average carbon dioxide level'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'average-co2-over-time',
      measurements: ['indoor_climate_10_minutes.co2_avg'],
      title: {
        sv: 'Koldioxidhalt över tid',
        en: 'Carbon dioxide level over time'
      },
      info: {
        sv: 'Visar koldioxidhalt över tid.',
        en: 'Shows carbon dioxide level over time.'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'average-co2-over-time-per-entity',
      measurements: ['indoor_climate_10_minutes.co2_avg'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }]
          }
        }
      },
      title: {
        sv: 'Koldioxidhalt över tid (per entitet)',
        en: 'Carbon dioxide level over time (per entity)'
      },
      info: {
        sv: 'Visar koldioxidhalt över tid.',
        en: 'Shows carbon dioxide level over time.'
      }
    },
    {
      type: DashboardComponentType.Histogram,
      name: 'co2-histogram',
      measurements: ['indoor_climate_10_minutes.co2_hist'],
      title: {
        sv: 'Koldioxidfördelning',
        en: 'Carbon dioxide distribution'
      },
      info: {
        sv: 'Visar koldioxidfördelning.',
        en: 'Shows carbon dioxide distribution.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'average-co2-toplist',
      measurements: ['indoor_climate_10_minutes.co2_avg'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'co2_avg', direction: 'DESC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: {
        sv: 'Koldioxidhalt per entitet',
        en: 'Carbon dioxide level per entity'
      },
      info: {
        sv: 'Visar koldioxidhalt per entitet.',
        en: 'Shows carbon dioxide level per entity.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'stddev-co2-toplist',
      measurements: ['indoor_climate_10_minutes.co2_stddev'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'co2_stddev', direction: 'DESC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: {
        sv: 'Koldioxidavvikelse per entitet',
        en: 'Carbon dioxide deviation per entity'
      },
      info: {
        sv: 'Visar koldioxidavvikelse per entitet.',
        en: 'Shows carbon dioxide deviation per entity.'
      }
    }
  ],
  tabs: [
    {
      name: 'temperature',
      title: {
        sv: 'Temperatur',
        en: 'Temperature'
      },
      components: [
        'average-temperature',
        'deviation-temperature',
        'average-temp-over-time',
        'average-temp-over-time-per-entity',
        'temp-histogram',
        'average-temp-toplist',
        'stddev-temp-toplist'
      ],
      initialShowOnMapComponent: 'average-temperature'
    },
    {
      name: 'humidity',
      title: {
        sv: 'Luftfuktighet',
        en: 'Humidity'
      },
      components: [
        'average-humidity',
        'average-humidity-over-time',
        'average-humidity-over-time-per-entity',
        'humidity-histogram',
        'average-humidity-toplist',
        'stddev-humidity-toplist'
      ],
      initialShowOnMapComponent: 'average-humidity'
    },
    {
      name: 'co2',
      title: {
        sv: 'Koldioxid',
        en: 'Carbon dioxide'
      },
      components: [
        'average-co2',
        'average-co2-over-time',
        'average-co2-over-time-per-entity',
        'co2-histogram',
        'average-co2-toplist',
        'stddev-co2-toplist'
      ],
      initialShowOnMapComponent: 'average-co2'
    }
  ]
})
